// BrokersPage — Connected brokers / data providers
// Layout:
//   - Top nav: < Back | "Connected brokers"
//   - Hero: X of Y connected
//   - Connected list with status
//   - Available to connect
//   - "Add new connection" CTA

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PartnerLogo, PlatformLogo } from '../../components/Brand';

interface Connection {
  id: string;
  name: string;
  type: 'platform' | 'data' | 'broker';
  status: 'active' | 'pending' | 'error' | 'disconnected';
  lastSync: string;
  account: string;
  version?: string;
}

const CONNECTED: Connection[] = [
  { id: 'c1', name: 'Deepchart',     type: 'platform', status: 'active',     lastSync: '2 min ago',  account: 'DC-7382', version: 'v3.2.1' },
  { id: 'c2', name: 'Rithmic',       type: 'data',     status: 'active',     lastSync: '12 sec ago', account: 'RT-A8419', version: 'API 2.0' },
  { id: 'c3', name: 'TwelveData',    type: 'data',     status: 'active',     lastSync: '5 sec ago',  account: 'TD-7C29', version: 'Pro plan' },
  { id: 'c4', name: 'TradingView',   type: 'data',     status: 'pending',    lastSync: 'Never',      account: '—',       version: 'OAuth' },
];

const AVAILABLE: Connection[] = [
  { id: 'a1', name: 'NinjaTrader',   type: 'platform', status: 'disconnected', lastSync: 'Never', account: '—', version: 'v8.1' },
  { id: 'a2', name: 'Tradovate',     type: 'broker',   status: 'disconnected', lastSync: 'Never', account: '—', version: 'WebSocket' },
  { id: 'a3', name: 'Sierra Chart',  type: 'platform', status: 'disconnected', lastSync: 'Never', account: '—', version: 'v23' },
  { id: 'a4', name: 'Quantower',     type: 'platform', status: 'disconnected', lastSync: 'Never', account: '—', version: 'v1.5' },
  { id: 'a5', name: 'ATAS',          type: 'platform', status: 'disconnected', lastSync: 'Never', account: '—', version: 'v3.18' },
  { id: 'a6', name: 'Bookmap',       type: 'platform', status: 'disconnected', lastSync: 'Never', account: '—', version: 'v7.3' },
];

const STATUS_META: Record<Connection['status'], { color: string; label: string; icon: string }> = {
  active:       { color: '#34C759', label: 'Active',       icon: '●' },
  pending:      { color: '#FF9500', label: 'Pending',      icon: '◐' },
  error:        { color: '#FF3B30', label: 'Error',        icon: '○' },
  disconnected: { color: '#8E8E93', label: 'Not connected', icon: '○' },
};

const TYPE_META: Record<Connection['type'], { label: string; color: string }> = {
  platform: { label: 'Trading platform', color: '#007AFF' },
  data:     { label: 'Data provider',    color: '#34C759' },
  broker:   { label: 'Brokerage',         color: '#FF9500' },
};

export function BrokersPage() {
  const nav = useNavigate();
  const [connecting, setConnecting] = useState<string | null>(null);

  function connect(id: string) {
    setConnecting(id);
    setTimeout(() => {
      alert('Connection flow for ' + id + ' would open here');
      setConnecting(null);
    }, 600);
  }

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Brokers & platforms</div>
        <button className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 5v14M5 12h14" strokeLinecap="round" /></svg>
        </button>
      </div>

      {/* Hero */}
      <div className="mx-4 mt-4">
        <div className="ios26-hero">
          <div className="flex items-center gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-[16px] text-[32px]" style={{ background: 'linear-gradient(135deg, #007AFF 0%, #5856D6 100%)' }}>🔌</div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-tf-text-3">Connected</div>
              <div className="dynamic-title text-tf-text-1 tabular-nums">{CONNECTED.filter((c) => c.status === 'active').length} <span className="text-[16px] text-tf-text-3 font-normal">/ {CONNECTED.length + AVAILABLE.length}</span></div>
              <div className="text-[12px] text-tf-text-3">Live data flowing ✓</div>
            </div>
          </div>
        </div>
      </div>

      {/* Connected */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Active connections</h2>
      <div className="mx-4 space-y-2">
        {CONNECTED.map((c) => <ConnectionCard key={c.id} connection={c} />)}
      </div>

      {/* Available */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Available to connect</h2>
      <div className="mx-4 space-y-2">
        {AVAILABLE.map((c) => <ConnectionCard key={c.id} connection={c} onConnect={() => connect(c.name)} connecting={connecting === c.name} />)}
      </div>

      <p className="mt-3 px-4 text-center text-[11px] text-tf-text-3">
        Don't see your platform? <button className="text-tf-blue font-semibold">Request integration</button>
      </p>
    </div>
  );
}

function ConnectionCard({ connection: c, onConnect, connecting }: { connection: Connection; onConnect?: () => void; connecting?: boolean }) {
  const statusMeta = STATUS_META[c.status];
  const typeMeta = TYPE_META[c.type];
  return (
    <div data-testid={`conn-${c.id}`} className="ios26-card p-3">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[12px] border border-tf-border bg-white">
          {c.type === 'data' ? <PartnerLogo name={c.name.toLowerCase().replace(/\s/g, '')} height={24} /> : <PlatformLogo name={c.name.toLowerCase().replace(/\s/g, '')} size={26} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[14px] font-bold text-tf-text-1">{c.name}</span>
            <span className="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide" style={{ backgroundColor: typeMeta.color + '20', color: typeMeta.color }}>{typeMeta.label}</span>
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-[11px]">
            <span style={{ color: statusMeta.color }}>{statusMeta.icon}</span>
            <span className="font-semibold" style={{ color: statusMeta.color }}>{statusMeta.label}</span>
            <span className="text-tf-text-3">· {c.lastSync}</span>
          </div>
          <div className="text-[10px] text-tf-text-3">{c.account} · {c.version}</div>
        </div>
        {c.status === 'active' ? (
          <button className="rounded-full bg-tf-surface-1 px-3 py-1 text-[11px] font-semibold text-tf-text-1 active:opacity-40">Manage</button>
        ) : c.status === 'pending' ? (
          <button className="rounded-full bg-[#FF9500]/15 px-3 py-1 text-[11px] font-semibold text-tf-warning">Continue</button>
        ) : (
          <button
            onClick={onConnect}
            disabled={connecting}
            data-testid={`connect-${c.id}`}
            className="rounded-full bg-tf-blue px-3 py-1 text-[11px] font-semibold text-white active:opacity-40 disabled:opacity-50"
          >
            {connecting ? 'Connecting…' : 'Connect'}
          </button>
        )}
      </div>
    </div>
  );
}
